import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../context/AdminAuthContext";
import {
  adminFetchOrders,
  adminFetchContacts,
  adminFetchSummary,
  adminMarkShipped,
  adminUnmarkShipped,
  adminMarkMessageRead,
} from "../lib/api";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const Money = ({ value }) => (
  <span className="font-serif gold-text">${Number(value || 0).toFixed(2)}</span>
);

const Stat = ({ label, value, testid }) => (
  <div className="border hairline p-6 bg-[#0a0a0a]" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">{label}</div>
    <div className="mt-3 font-serif text-3xl text-[var(--primary-text)]">{value}</div>
  </div>
);

const OrderCard = ({ order, onShip, onUnship, busyId }) => {
  const addr = order.shipping?.address || {};
  const shipped = order.fulfillment_status === "shipped";
  const isBusy = busyId === order.id;
  return (
    <div className="border hairline p-6 bg-[#0a0a0a]" data-testid={`order-card-${order.order_number}`}>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-label text-[var(--accent-gold)]">Order</div>
          <div className="mt-1 font-serif text-xl">#{order.order_number}</div>
          <div className="mt-1 text-xs text-[var(--secondary-text)]">{formatDate(order.created_at)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">Total</div>
          <div className="mt-1 text-2xl"><Money value={order.amount} /></div>
          <div className={`mt-2 inline-block text-[10px] uppercase tracking-label px-2 py-1 border hairline ${shipped ? "text-[var(--accent-gold)]" : "text-[var(--secondary-text)]"}`}>
            {shipped ? "Shipped" : "Pending"}
          </div>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-label text-[var(--secondary-text)] mb-2">Ship to</div>
          <div className="text-sm text-[var(--primary-text)] leading-relaxed" data-testid={`order-shipping-${order.order_number}`}>
            <div className="font-medium">{order.customer_name || "—"}</div>
            <div>{addr.line1}</div>
            {addr.line2 && <div>{addr.line2}</div>}
            <div>{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}</div>
            <div>{addr.country}</div>
          </div>
          <div className="mt-3 text-xs text-[var(--secondary-text)]">
            <div>{order.customer_email}</div>
            {order.customer_phone && <div>{order.customer_phone}</div>}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-label text-[var(--secondary-text)] mb-2">Items</div>
          <ul className="text-sm text-[var(--primary-text)] space-y-1">
            {(order.items || []).map((it, idx) => (
              <li key={idx} className="flex justify-between">
                <span>{it.name} × {it.qty}</span>
                <span className="text-[var(--secondary-text)]">${(it.price * it.qty).toFixed(2)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex gap-3">
            {shipped ? (
              <button
                onClick={() => onUnship(order.id)}
                disabled={isBusy}
                className="btn-secondary text-xs disabled:opacity-50"
                data-testid={`unship-btn-${order.order_number}`}
              >
                {isBusy ? "…" : "Mark as pending"}
              </button>
            ) : (
              <button
                onClick={() => onShip(order.id)}
                disabled={isBusy}
                className="btn-primary text-xs disabled:opacity-50"
                data-testid={`ship-btn-${order.order_number}`}
              >
                {isBusy ? "…" : "Mark as shipped"}
              </button>
            )}
            <button
              onClick={() => {
                const text = [
                  order.customer_name,
                  addr.line1,
                  addr.line2,
                  `${addr.city || ""}, ${addr.state || ""} ${addr.postal_code || ""}`,
                  addr.country,
                ].filter(Boolean).join("\n");
                navigator.clipboard?.writeText(text);
              }}
              className="btn-secondary text-xs"
              data-testid={`copy-address-${order.order_number}`}
            >
              Copy address
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageCard = ({ msg, onMarkRead }) => (
  <div className="border hairline p-6 bg-[#0a0a0a]" data-testid={`message-card-${msg.id}`}>
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="font-serif text-lg">{msg.name}</div>
        <a href={`mailto:${msg.email}`} className="text-xs text-[var(--accent-gold)] tracking-wide">{msg.email}</a>
        <div className="mt-1 text-xs text-[var(--secondary-text)]">{formatDate(msg.created_at)}</div>
      </div>
      <div className="text-right">
        {!msg.read && (
          <span className="text-[10px] uppercase tracking-label text-[var(--accent-gold)] border hairline px-2 py-1">New</span>
        )}
      </div>
    </div>
    {msg.subject && (
      <div className="mt-4 text-[10px] uppercase tracking-label text-[var(--secondary-text)]">Subject</div>
    )}
    {msg.subject && <div className="text-sm text-[var(--primary-text)]">{msg.subject}</div>}
    <div className="mt-4 text-[10px] uppercase tracking-label text-[var(--secondary-text)]">Message</div>
    <div className="mt-1 text-sm text-[var(--primary-text)] whitespace-pre-wrap leading-relaxed">{msg.message}</div>
    {!msg.read && (
      <button
        onClick={() => onMarkRead(msg.id)}
        className="btn-secondary text-xs mt-5"
        data-testid={`mark-read-${msg.id}`}
      >
        Mark as read
      </button>
    )}
  </div>
);

const AdminDashboard = () => {
  const { status, email, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");

  useEffect(() => {
    if (status === "unauthenticated") navigate("/admin/login", { replace: true });
  }, [status, navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [o, m, s] = await Promise.all([
        adminFetchOrders(),
        adminFetchContacts(),
        adminFetchSummary(),
      ]);
      setOrders(o.orders || []);
      setMessages(m.messages || []);
      setSummary(s);
    } catch (e) {
      // token expired or other — context will redirect
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "authenticated") loadAll();
  }, [status, loadAll]);

  if (status !== "authenticated") {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--secondary-text)]">
        <div className="text-xs uppercase tracking-label">Checking session…</div>
      </div>
    );
  }

  const onShip = async (id) => {
    setBusyId(id);
    try {
      const res = await adminMarkShipped(id);
      setOrders((prev) => prev.map((o) => (o.id === id ? res.order : o)));
      const s = await adminFetchSummary();
      setSummary(s);
    } finally {
      setBusyId(null);
    }
  };
  const onUnship = async (id) => {
    setBusyId(id);
    try {
      const res = await adminUnmarkShipped(id);
      setOrders((prev) => prev.map((o) => (o.id === id ? res.order : o)));
      const s = await adminFetchSummary();
      setSummary(s);
    } finally {
      setBusyId(null);
    }
  };
  const onMarkRead = async (id) => {
    await adminMarkMessageRead(id);
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    const s = await adminFetchSummary();
    setSummary(s);
  };

  const filteredOrders = orders.filter((o) => {
    if (orderFilter === "all") return true;
    if (orderFilter === "pending") return o.fulfillment_status !== "shipped";
    if (orderFilter === "shipped") return o.fulfillment_status === "shipped";
    return true;
  });

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[var(--bg)] text-[var(--primary-text)]">
      <header className="border-b hairline">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img src="/mountain-mark.png" alt="" className="h-6 w-auto" />
            <div>
              <div className="text-[10px] uppercase tracking-label text-[var(--accent-gold)]">Summit Scents</div>
              <div className="font-serif text-xl">Maison Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[var(--secondary-text)] hidden md:inline">{email}</span>
            <button onClick={loadAll} className="btn-secondary text-xs" data-testid="admin-refresh">Refresh</button>
            <button onClick={logout} className="btn-secondary text-xs" data-testid="admin-logout">Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <Stat label="Total Orders" value={summary.total_orders} testid="stat-total-orders" />
            <Stat label="Pending" value={summary.pending_orders} testid="stat-pending" />
            <Stat label="Shipped" value={summary.shipped_orders} testid="stat-shipped" />
            <Stat label="Revenue" value={`$${Number(summary.total_revenue || 0).toFixed(2)}`} testid="stat-revenue" />
          </div>
        )}

        <div className="flex items-center gap-6 border-b hairline mb-8">
          <button
            onClick={() => setTab("orders")}
            className={`pb-4 text-[11px] uppercase tracking-label transition-colors ${tab === "orders" ? "text-[var(--accent-gold)] border-b border-[var(--accent-gold)] -mb-px" : "text-[var(--secondary-text)] hover:text-[var(--primary-text)]"}`}
            data-testid="tab-orders"
          >
            Orders ({orders.length})
          </button>
          <button
            onClick={() => setTab("messages")}
            className={`pb-4 text-[11px] uppercase tracking-label transition-colors ${tab === "messages" ? "text-[var(--accent-gold)] border-b border-[var(--accent-gold)] -mb-px" : "text-[var(--secondary-text)] hover:text-[var(--primary-text)]"}`}
            data-testid="tab-messages"
          >
            Messages ({messages.length}){summary?.unread_messages > 0 && (
              <span className="ml-2 text-[var(--accent-gold)]">• {summary.unread_messages} new</span>
            )}
          </button>
        </div>

        {loading && (
          <div className="text-xs uppercase tracking-label text-[var(--secondary-text)]">Loading…</div>
        )}

        {!loading && tab === "orders" && (
          <div>
            <div className="flex items-center gap-3 mb-6 text-[10px] uppercase tracking-label">
              {["all", "pending", "shipped"].map((f) => (
                <button
                  key={f}
                  onClick={() => setOrderFilter(f)}
                  className={`px-3 py-1 border hairline transition-colors ${orderFilter === f ? "text-[var(--accent-gold)] border-[var(--accent-gold)]" : "text-[var(--secondary-text)]"}`}
                  data-testid={`filter-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>
            {filteredOrders.length === 0 ? (
              <div className="border hairline p-12 text-center text-[var(--secondary-text)]" data-testid="no-orders">
                <div className="text-xs uppercase tracking-label">No orders yet</div>
                <div className="mt-2 text-sm">Completed purchases will appear here with shipping details.</div>
              </div>
            ) : (
              <div className="space-y-5">
                {filteredOrders.map((o) => (
                  <OrderCard key={o.id} order={o} onShip={onShip} onUnship={onUnship} busyId={busyId} />
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && tab === "messages" && (
          <div>
            {messages.length === 0 ? (
              <div className="border hairline p-12 text-center text-[var(--secondary-text)]" data-testid="no-messages">
                <div className="text-xs uppercase tracking-label">No messages</div>
              </div>
            ) : (
              <div className="space-y-5">
                {messages.map((m) => (
                  <MessageCard key={m.id} msg={m} onMarkRead={onMarkRead} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
